import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function ContactNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/98 backdrop-blur-lg shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="w-full px-6 lg:px-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer group relative">
            <img
              src="https://static.readdy.ai/image/bff3983fcac7598cf15bb5173700e0d7/b58838733802ef1d20431284565f42b3.jpeg"
              alt="House of FMR"
              className="h-12 w-12 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block">
              <p className={`font-display text-xl tracking-wide transition-colors duration-300 ${scrolled ? 'text-stone-800' : 'text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-300'}`}>
                House of FMR
              </p>
              <p className="text-amber-400/80 text-[10px] tracking-[0.25em] uppercase font-medium">Flair • Mixology • Rentals</p>
            </div>
            {/* FMR Tooltip */}
            <div className="absolute top-full left-0 mt-2 px-3 py-1.5 bg-stone-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
              Flair • Mixology • Rentals
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {[
              { label: 'Home', to: '/' },
              { label: 'Services', to: '/services' },
              { label: 'Equipment Hire', to: '/equipment-hire' },
              { label: 'Gallery', to: '/gallery' },
              { label: 'Shop', to: '/shop' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isActive(item.to)
                    ? 'text-amber-600 font-semibold'
                    : scrolled 
                      ? 'text-stone-600 hover:text-amber-600' 
                      : 'text-white/90 hover:text-amber-200'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className={`text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-amber-600' : 'text-amber-300'
              }`}
            >
              Contact Us
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:0411164144"
              className={`flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-stone-600 hover:text-amber-600' : 'text-white/90 hover:text-amber-200'
              }`}
            >
              <i className="ri-phone-line"></i>
              0411 164 144
            </a>
          </div>

          <button
            className={`lg:hidden w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all ${
              scrolled ? 'bg-stone-100 text-stone-900' : 'bg-white/10 text-white border border-amber-300/20'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="lg:hidden mt-4 pb-4 bg-white rounded-2xl p-5 shadow-2xl border border-amber-100">
            <div className="space-y-1">
              {[
                { label: 'Home', to: '/', icon: 'ri-home-line' },
                { label: 'Services', to: '/services', icon: 'ri-service-line' },
                { label: 'Equipment Hire', to: '/equipment-hire', icon: 'ri-calendar-check-line' },
                { label: 'Gallery', to: '/gallery', icon: 'ri-image-line' },
                { label: 'Shop', to: '/shop', icon: 'ri-store-2-line' },
                { label: 'Contact Us', to: '/contact', icon: 'ri-mail-line' },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 w-full text-left px-4 py-3.5 text-sm font-medium rounded-lg transition-colors cursor-pointer ${
                    isActive(item.to)
                      ? 'text-amber-600 bg-amber-50 font-semibold'
                      : 'text-stone-700 hover:text-amber-600 hover:bg-amber-50'
                  }`}
                >
                  <i className={`${item.icon} text-base`}></i>
                  {item.label}
                </Link>
              ))}
              <div className="h-px bg-stone-200 my-2"></div>
              <a
                href="tel:0411164144"
                className="flex items-center justify-center gap-2 w-full px-4 py-3.5 bg-stone-100 text-stone-700 text-sm font-semibold rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-base"></i>Call 0411 164 144
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}